/**
 * career-app — the shell (layout), DNA-aligned dark chrome (charcoal + amber).
 * ONE chrome palette across the whole frame — top bar, left pane, right pane and
 * the footer pipeline bar all read as the same warm-charcoal surface with the amber
 * accent; only the content (<kit-outlet>) follows the light/dark theme.
 *
 * TOP BAR (.shell-head): a far-left **left-pane toggle**, the brand (bearing mark +
 * Fraunces wordmark), a **breadcrumb** (App-Space › Workspace › …surface crumbs),
 * and a far-right **AI-agent toggle** (opens the right pane). Nothing between the
 * breadcrumb and the AI toggle, so the two controls can never collide on mobile.
 *
 * FOOTER (.shell-foot): the **primary value pipeline** — the four app-spaces
 * (Workshops · Kompass · Suche · Bewerbungen), `aria-current` on the active one,
 * thumb-reachable on mobile. This is how you move ACROSS the pipeline / switch
 * context. The url-safe **resource-path** slug is pinned to its right (desktop only).
 *
 * LEFT PANE (.shell-nav): **context-dependent** navigation WITHIN the current
 * app-space — a space header + the sections of that space (Kompass → Profil ·
 * Lebenslauf · Belege · Nachweise, …), each able to carry an **indicator** badge
 * (count + severity, e.g. open gaps). Collapsible to an icon rail; the account
 * **avatar** sits at the foot (IdP picture or initials) and its popover is the home
 * for everything non-pipeline (Läufe, KI-Einstellungen, Theme, Onboarding, Abmelden).
 * The shell owns a sensible DEFAULT section set per space; the active surface may
 * REPLACE it (and attach live indicator counts) through the SectionNavBus.
 *
 * RIGHT PANE (.shell-aside): the **Platform AI Agent** panel — the plattform-wide
 * agent with access to all Platform Applications the user holds (Career Studio being
 * one; the list grows as access is granted). Toggled from the top bar's AI control.
 *
 * The breadcrumb is extensible: the shell always renders `App-Space › Workspace`;
 * the ACTIVE surface may push deeper crumbs (Sub-area, Step) through the
 * BreadcrumbBus. The <kit-outlet> mounts the active surface. No urn — a layout element.
 */

import {
  KitElement,
  effect,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type RouteMatch,
  type Router,
  type Signal,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, ifDefined, nothing, svg, type TemplateResult } from "@platform/web-kit/render"
import {
  BreadcrumbToken,
  I18nToken,
  IngestStoreToken,
  RouterToken,
  SectionNavToken,
  SessionToken,
  apiGet,
  fetchWallet,
  CREDITS_UPDATED_EVENT,
  PAYMENT_REQUIRED_EVENT,
  type WalletView,
} from "../services"
import type { CareerI18n, CareerKey } from "../../i18n"
import {
  SETTINGS_GATE_ROLE,
  sessionCanConfigureLlm,
  type BreadcrumbBus,
  type Crumb,
  type IngestRunView,
  type IngestStore,
  type SectionNavBus,
  type SectionNavItem,
  type SectionTone,
  type ShellSession,
} from "../domain"

/** id of the visually-hidden hint that gated user-menu links point at via aria-describedby. */
const GATED_HINT_ID = "career-gated-hint"
/** id of the top-right user-menu popover (native Popover API). */
const USER_POP_ID = "career-user-pop"
/** localStorage key the FOUC-guard script (index.html) and the toggle share. */
const THEME_KEY = "career-theme"
/** localStorage keys for the shell chrome layout (left sidebar collapse, right pane open). */
const NAV_KEY = "career-nav-collapsed"
const ASIDE_KEY = "career-aside-open"

type ThemeMode = "light" | "dark" | "auto"

/** Read a boolean layout flag from storage; default `fallback`. Storage-safe (SSR/test). */
function readFlag(key: string, fallback: boolean): boolean {
  try {
    const v = globalThis.localStorage?.getItem(key)
    if (v === "1") return true
    if (v === "0") return false
  } catch {
    // storage unavailable — use the default.
  }
  return fallback
}

/** Up to two uppercase initials from a display name (avatar fallback when no picture). */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return "?"
  const first = parts[0]![0] ?? ""
  const last = parts.length > 1 ? (parts.at(-1)![0] ?? "") : ""
  return (first + last).toUpperCase()
}

interface NavItem {
  url: string
  /** Active-state path prefix. */
  match: string
  label: CareerKey
  /** Realm role required, else the entry is marked gated. */
  requiresRole?: string
  /** Custom gate predicate (role + org, etc.); when set, it decides the gated cue. */
  gate?: (session: ShellSession) => boolean
  /** Shown only once an ingest run exists (the review destination). */
  requiresRun?: boolean
}

// URL paths are ENGLISH code (AX-9); the `label` keys resolve to German link TEXT.
// The FOOTER pipeline bar shows EXACTLY these four PRIMARY app-spaces (§7,
// operator-ratified order): Workshops · Kompass · Suche · Bewerbungen — the primary
// way to move ACROSS the value pipeline / switch context.
const PRIMARY_NAV: readonly NavItem[] = [
  { url: "/workshops", match: "/workshops", label: "nav.workshops" },
  { url: "/compass", match: "/compass", label: "nav.kompass" },
  { url: "/search", match: "/search", label: "nav.suche" },
  { url: "/applications", match: "/applications", label: "nav.bewerbungen" },
]

// Everything that is NOT a primary pipeline stage lives in the account popover at the
// foot of the left pane. Dev-only diagnostics (the push-plane heartbeat) are NOT user
// destinations.
const USER_MENU_NAV: readonly NavItem[] = [
  { url: "/credits", match: "/credits", label: "nav.guthaben" },
  { url: "/runs", match: "/runs", label: "nav.laeufe" },
  { url: "/settings/ai", match: "/settings", label: "nav.einstellungen", requiresRole: SETTINGS_GATE_ROLE, gate: sessionCanConfigureLlm },
  { url: "/onboarding", match: "/onboarding", label: "nav.onboarding" },
  { url: "/onboarding/review", match: "/onboarding/review", label: "nav.review", requiresRun: true },
]

/** A default within-space section entry (label + icon are resolved at render). */
interface SectionDef {
  url: string
  match: string
  label: CareerKey
  icon: SectionIcon
  indicator?: { count: number; tone: SectionTone }
}
type SectionIcon = "overview" | "profile" | "cv" | "evidence" | "assets" | "workshop" | "upload" | "review" | "runs" | "settings" | "credits"

/** A left-pane section group: the sections of ONE app-space (or intake/account route). */
interface SectionGroup {
  /** The path prefix that selects this group. */
  prefix: string
  /** The space title shown as the pane header (an existing nav label key). */
  titleKey: CareerKey
  items: readonly SectionDef[]
}

// The shell's DEFAULT section set per space. Kompass is the rich case (five sections);
// spaces without a deeper sub-nav yet show their primary section. A surface may REPLACE
// its group's items — and attach LIVE indicator counts — via the SectionNavBus.
const SECTION_GROUPS: readonly SectionGroup[] = [
  {
    prefix: "/workshops",
    titleKey: "nav.workshops",
    items: [
      { url: "/workshops", match: "/workshops", label: "sectionnav.overview", icon: "overview" },
      { url: "/workshops/self-knowledge", match: "/workshops/self-knowledge", label: "sectionnav.ws.self", icon: "workshop" },
      { url: "/workshops/search-profile", match: "/workshops/search-profile", label: "sectionnav.ws.search", icon: "workshop" },
      { url: "/workshops/profiler", match: "/workshops/profiler", label: "sectionnav.ws.profiler", icon: "workshop" },
    ],
  },
  {
    prefix: "/compass",
    titleKey: "nav.kompass",
    items: [
      { url: "/compass", match: "/compass", label: "sectionnav.overview", icon: "overview" },
      { url: "/compass/profile", match: "/compass/profile", label: "surface.profil.title", icon: "profile" },
      { url: "/compass/cv", match: "/compass/cv", label: "surface.lebenslauf.title", icon: "cv" },
      // No hardcoded indicator: a fabricated count would contradict the real "N offen" the
      // Belege surface shows (evidence-grade honesty). The affordance + badge rendering are
      // REAL — the evidence surface publishes a LIVE open-gap count (tone by severity) through
      // the SectionNavBus (seam in app/domain.ts); the badge appears once that count is wired.
      { url: "/compass/evidence", match: "/compass/evidence", label: "surface.belege.title", icon: "evidence" },
      { url: "/compass/assets", match: "/compass/assets", label: "surface.nachweise.title", icon: "assets" },
    ],
  },
  {
    prefix: "/search",
    titleKey: "nav.suche",
    items: [{ url: "/search", match: "/search", label: "sectionnav.overview", icon: "overview" }],
  },
  {
    prefix: "/applications",
    titleKey: "nav.bewerbungen",
    items: [{ url: "/applications", match: "/applications", label: "sectionnav.overview", icon: "overview" }],
  },
  {
    prefix: "/onboarding",
    titleKey: "nav.onboarding",
    items: [
      { url: "/onboarding", match: "/onboarding", label: "sectionnav.onboarding.upload", icon: "upload" },
      { url: "/onboarding/review", match: "/onboarding/review", label: "nav.review", icon: "review" },
    ],
  },
  {
    prefix: "/credits",
    titleKey: "nav.guthaben",
    items: [{ url: "/credits", match: "/credits", label: "sectionnav.overview", icon: "credits" }],
  },
  {
    prefix: "/runs",
    titleKey: "nav.laeufe",
    items: [{ url: "/runs", match: "/runs", label: "sectionnav.overview", icon: "runs" }],
  },
  {
    prefix: "/settings",
    titleKey: "nav.einstellungen",
    items: [{ url: "/settings/ai", match: "/settings/ai", label: "sectionnav.overview", icon: "settings" }],
  },
]

const THEME_MODES: readonly ThemeMode[] = ["light", "dark", "auto"]
const THEME_LABEL: Record<ThemeMode, CareerKey> = {
  light: "theme.light",
  dark: "theme.dark",
  auto: "theme.auto",
}

/** True on a phone-width viewport (the left pane is an overlay drawer, not an inline rail).
 *  Storage/SSR/test-safe — defaults to false (desktop) when matchMedia is unavailable. */
function isMobileViewport(): boolean {
  try {
    return globalThis.matchMedia?.("(max-width: 720px)")?.matches ?? false
  } catch {
    return false
  }
}

/** Read the persisted theme mode; default "auto" (follow OS). Storage-safe. */
function readThemeMode(): ThemeMode {
  try {
    const m = globalThis.localStorage?.getItem(THEME_KEY)
    if (m === "light" || m === "dark") return m
  } catch {
    // storage unavailable (SSR/test) — fall through to auto.
  }
  return "auto"
}

/**
 * ONE coherent stroke icon set, ported verbatim from the prior project's set
 * (apps/career-studio/app/components/ui/icons.tsx) so every glyph in the shell — the footer
 * pipeline, the left-pane sections, and the two chrome toggles — speaks one visual language.
 * Each entry is an `svg`-tagged FRAGMENT (bare children) dropped into a `viewBox="0 0 24 24"`
 * wrapper; stroke width / colour come from the surrounding CSS (`* svg { stroke: currentColor }`).
 */
const GLYPH = {
  upload: svg`<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />`,
  workshops: svg`<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />`,
  compass: svg`<circle cx="12" cy="12" r="10" /><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />`,
  search: svg`<circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />`,
  applications: svg`<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" />`,
  runs: svg`<polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />`,
  settings: svg`<circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />`,
  user: svg`<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" />`,
  // Wallet — the credits chip + the /credits space glyph.
  wallet: svg`<path d="M3 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" /><path d="M16 12h3v-2h-3a1 1 0 0 0 0 2Z" />`,
  cv: svg`<path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2Z" /><path d="M8 13h8M8 17h5" />`,
  evidence: svg`<path d="M12 3 4 6v6c0 4 3.5 7 8 9 4.5-2 8-5 8-9V6l-8-3Z" /><path d="m9 12 2 2 4-4" />`,
  assets: svg`<path d="M21 12.5V7a2 2 0 0 0-2-2h-6l-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8" /><path d="m16 16 3 3 4-4" />`,
  review: svg`<path d="m9 11 3 3 8-8" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />`,
  overview: svg`<rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" />`,
  // Sidebar-panel glyphs for the two chrome toggles — same rect, divider mirrored (near-left / near-right).
  sidebarLeft: svg`<rect x="3" y="3" width="18" height="18" rx="2" /><line x1="9" y1="3" x2="9" y2="21" />`,
  sidebarRight: svg`<rect x="3" y="3" width="18" height="18" rx="2" /><line x1="15" y1="3" x2="15" y2="21" />`,
} as const

/** A compact stroke icon per pipeline space (18px, currentColor) for the footer bar. */
function navIcon(label: CareerKey): TemplateResult {
  const icons: Partial<Record<CareerKey, TemplateResult>> = {
    "nav.onboarding": GLYPH.upload,
    "nav.review": GLYPH.review,
    "nav.workshops": GLYPH.workshops,
    "nav.kompass": GLYPH.compass,
    "nav.suche": GLYPH.search,
    "nav.bewerbungen": GLYPH.applications,
    "nav.laeufe": GLYPH.runs,
    "nav.einstellungen": GLYPH.settings,
    "nav.guthaben": GLYPH.wallet,
    "nav.profil": GLYPH.user,
  }
  return html`<svg viewBox="0 0 24 24" aria-hidden="true">${icons[label] ?? svg`<circle cx="12" cy="12" r="4" />`}</svg>`
}

/** A compact stroke icon per left-pane SECTION (16px). Unknown key → a neutral dot. */
function sectionIconGlyph(icon: string | undefined): TemplateResult {
  const icons: Record<SectionIcon, TemplateResult> = {
    overview: GLYPH.overview,
    profile: GLYPH.user,
    cv: GLYPH.cv,
    evidence: GLYPH.evidence,
    assets: GLYPH.assets,
    workshop: GLYPH.workshops,
    upload: GLYPH.upload,
    review: GLYPH.review,
    runs: GLYPH.runs,
    settings: GLYPH.settings,
    credits: GLYPH.wallet,
  }
  const glyph = icon && icon in icons ? icons[icon as SectionIcon] : svg`<circle cx="12" cy="12" r="3.5" />`
  return html`<svg viewBox="0 0 24 24" aria-hidden="true">${glyph}</svg>`
}

/** One footer pipeline-space link (`foot-item`) — icon + label, `aria-current` on active. */
function footAnchor(vm: ShellVm, item: NavItem): TemplateResult {
  const isActive = vm.isActive(item)
  return html`<a
    href=${item.url}
    data-act="nav"
    data-url=${item.url}
    class=${classMap({ "foot-item": true, "is-current": isActive })}
    aria-current=${ifDefined(isActive ? "page" : undefined)}
    title=${vm.t(item.label)}
    lang="de"
    >${navIcon(item.label)}<span class="foot-item-label">${vm.t(item.label)}</span></a
  >`
}

/** One left-pane section link (`nav-item`) — icon + label + optional indicator badge,
 *  `aria-current` on the active section. Label folds away when the pane collapses to a rail. */
function sectionAnchor(vm: ShellVm, item: SectionNavItem): TemplateResult {
  const isActive = vm.isSectionActive(item)
  const ind = item.indicator
  return html`<a
    href=${item.url}
    data-act="nav"
    data-url=${item.url}
    class=${classMap({ "nav-item": true, "is-current": isActive })}
    aria-current=${ifDefined(isActive ? "page" : undefined)}
    title=${item.label}
    lang="de"
    >${sectionIconGlyph(item.icon)}<span class="nav-item-label">${item.label}</span>${
      ind
        ? html`<span
            class=${classMap({ "nav-badge": true, [`is-${ind.tone}`]: true })}
            title=${ifDefined(ind.label)}
            aria-label=${ifDefined(ind.label)}
            >${ind.count}</span
          >`
        : nothing
    }</a
  >`
}

/** One user-menu link (`user-item`) — carries active + gated (is-gated + hint) cues. */
function userMenuAnchor(vm: ShellVm, item: NavItem): TemplateResult {
  const isActive = vm.isActive(item)
  const gated = vm.isGated(item)
  return html`<a
    href=${item.url}
    data-act="nav"
    data-url=${item.url}
    data-gated=${ifDefined(gated ? "true" : undefined)}
    class=${classMap({ "user-item": true, "is-current": isActive, "is-gated": gated })}
    aria-current=${ifDefined(isActive ? "page" : undefined)}
    aria-describedby=${ifDefined(gated ? GATED_HINT_ID : undefined)}
    title=${ifDefined(gated ? vm.t("gated.hint") : undefined)}
    lang="de"
    >${navIcon(item.label)}<span class="user-item-label">${vm.t(item.label)}</span></a
  >`
}

export class ShellVm implements ViewModel {
  readonly current: ReadonlySignal<RouteMatch | null>
  /** Cold-load probe: a run may exist before any surface has subscribed the Ingest stream. */
  private readonly fetchedHasRun: Signal<boolean> = signal(false)
  /** Live: the push-fed ingest store gains a run the moment onboarding/review subscribes. */
  private readonly runs: ReadonlySignal<readonly IngestRunView[]>
  /** Active theme mode — drives the toggle's aria-pressed and the <html> data-theme. */
  private readonly theme: Signal<ThemeMode> = signal(readThemeMode())
  /** DESKTOP: the left pane collapsed to an icon rail (persisted). */
  private readonly navCollapsed: Signal<boolean> = signal(readFlag(NAV_KEY, false))
  /** MOBILE: the left pane overlay drawer open (ephemeral; default CLOSED — never open on load). */
  private readonly navDrawerOpen: Signal<boolean> = signal(false)
  /** Right AI-agent pane open (persisted; default closed). */
  private readonly asideOpen: Signal<boolean> = signal(readFlag(ASIDE_KEY, false))
  /** The credit wallet for the balance chip (fetched on attach; refreshed on a purchase). */
  private readonly wallet: Signal<WalletView | null> = signal<WalletView | null>(null)
  /** The shared credits notice is showing (402 insufficient OR entitlement-403 blocked). */
  private readonly paymentNotice: Signal<boolean> = signal(false)
  /** I7: the politely-announced destination title on a user-initiated route change (empty on load). */
  private readonly announcement: Signal<string> = signal("")
  /** I7: the path last settled — null until the FIRST route resolves (initial load: no focus/announce). */
  private lastAnnouncedPath: string | null = null
  /** I7: set on a user navigation; the element consumes it AFTER the outlet re-renders → focus <main>. */
  private focusMainPending = false
  /** Which copy the notice shows: `blocked` (zero-balance first run) vs `insufficient` (has some, not enough). */
  private readonly paymentNoticeKind: Signal<"insufficient" | "blocked"> = signal("insufficient")
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]
  /** Bound window listeners (removed on detach). */
  private readonly onPaymentRequired = (e: Event): void => {
    const kind = (e as CustomEvent<{ kind?: "insufficient" | "blocked" }>).detail?.kind
    this.paymentNoticeKind.set(kind === "blocked" ? "blocked" : "insufficient")
    this.paymentNotice.set(true)
    void this.loadWallet()
  }
  private readonly onCreditsUpdated = (): void => void this.loadWallet()

  constructor(
    private readonly router: Router,
    i18n: CareerI18n,
    readonly session: ShellSession,
    ingestStore: IngestStore,
    /** The surface-contributed deeper crumbs (Sub-area, Step) — reactive. */
    private readonly breadcrumb: BreadcrumbBus,
    /** The surface-contributed left-pane section nav — reactive. */
    private readonly sectionNav: SectionNavBus,
  ) {
    this.current = router.current
    this.t = i18n.t
    this.runs = ingestStore.list("aktiv")
    this.renderDeps = [
      this.current,
      this.fetchedHasRun,
      this.runs,
      this.theme,
      this.navCollapsed,
      this.navDrawerOpen,
      this.asideOpen,
      this.breadcrumb.crumbs,
      this.sectionNav.items,
      this.wallet,
      this.paymentNotice,
      this.sectionNav.indicators,
      this.announcement,
      this.paymentNoticeKind,
    ]
  }

  /** Learn whether an ingest run exists, so the review menu entry appears (M2). Both the
   *  cold-load fetch AND the live store feed it, so it appears in-session too. Also loads the
   *  balance chip and subscribes the shared 402 / purchase-landed signals (Step 5). */
  attach(): () => void {
    if (this.session.user) {
      void this.loadRuns()
      void this.loadWallet()
    }
    globalThis.addEventListener?.(PAYMENT_REQUIRED_EVENT, this.onPaymentRequired)
    globalThis.addEventListener?.(CREDITS_UPDATED_EVENT, this.onCreditsUpdated)
    // I7 (WCAG 2.4.3 / 4.1.3): on a USER-INITIATED route change — not the initial load,
    // not a same-URL re-render — announce the destination surface politely and flag focus
    // to move to <main> after the outlet re-renders. The initial load is left untouched so
    // the skip-link stays first-focusable; a popstate while a field is focused is STILL a
    // navigation, so it still moves focus (we only skip the very first settle).
    const stopRouteFx = effect([this.current], () => {
      const match = this.current.value
      if (!match) return // route not resolved yet — the shell can mount before router.start settles.
      const { path } = match
      if (this.lastAnnouncedPath === null) {
        this.lastAnnouncedPath = path // FIRST resolved route (initial load) — baseline, DON'T steal focus.
        return
      }
      if (path === this.lastAnnouncedPath) return // same-URL re-render — nothing to announce.
      this.lastAnnouncedPath = path
      this.announcement.set(this.routeTitle())
      this.focusMainPending = true
    })
    return () => {
      globalThis.removeEventListener?.(PAYMENT_REQUIRED_EVENT, this.onPaymentRequired)
      globalThis.removeEventListener?.(CREDITS_UPDATED_EVENT, this.onCreditsUpdated)
      stopRouteFx()
    }
  }

  /** I7: the polite live-region text — the last announced destination-surface title. */
  announcementText(): string {
    return this.announcement.value
  }

  /**
   * I7: the human title for the current route. Within a space, the active SECTION label
   * (e.g. "Belege", "Profil") — UNLESS it's the space-root / overview section (its match
   * equals the space prefix), where the app-space label ("Kompass", "Suche") reads better;
   * else the active app-space / user-menu label, else the app name. Reuses the SAME nav
   * machinery the section pane and breadcrumb already resolve — no parallel route→title map.
   */
  routeTitle(): string {
    const active = this.activeItem()
    const spaceLabel = active ? this.t(active.label) : this.t("app.name")
    const group = this.sectionGroup()
    const match = this.activeSectionMatch()
    if (group && match && match !== group.prefix) {
      const item = this.sectionItems().find((it) => it.match === match)
      if (item) return item.label
    }
    return spaceLabel
  }

  /** I7: consume the one pending post-navigation focus move (element calls this after render). */
  takePendingMainFocus(): boolean {
    if (!this.focusMainPending) return false
    this.focusMainPending = false
    return true
  }

  private async loadRuns(): Promise<void> {
    try {
      const runs = await apiGet<unknown[]>("/api/ingest")
      this.fetchedHasRun.set(runs.length > 0)
    } catch {
      // signed out or unreachable — leave the review entry hidden.
    }
  }

  /** Fetch the wallet for the balance chip; failure hides the chip (never a hard error). */
  private async loadWallet(): Promise<void> {
    try {
      this.wallet.set(await fetchWallet())
    } catch {
      // signed out, unreachable, or billing not yet live — the chip simply doesn't render.
    }
  }

  /** The current balance for the chip, or null when unknown (hides the chip). */
  walletBalance(): number | null {
    return this.wallet.value?.balance ?? null
  }

  /** Whether the shared credits notice is visible. */
  showPaymentNotice(): boolean {
    return this.paymentNotice.value
  }

  /** The notice copy key — `blocked` (nothing yet, get started) vs `insufficient` (not enough for this). */
  paymentNoticeKey(): CareerKey {
    return this.paymentNoticeKind.value === "blocked" ? "credits.notice.blocked" : "credits.notice.insufficient"
  }

  /** Dismiss the 402 notice (user acknowledged / navigated to top up). */
  dismissPaymentNotice(): void {
    this.paymentNotice.set(false)
  }

  private get hasRun(): boolean {
    return this.fetchedHasRun.value || this.runs.value.length > 0
  }

  get path(): string {
    return this.current.value?.path ?? "/"
  }

  /**
   * The url-safe SLUG path to the resource/asset in view — the footer resource-path (§7).
   * DISTINCT from the human breadcrumb: it addresses the resource itself in slug form (e.g.
   * `search/opportunities/abc123`), never a display-name. Derived from the route pathname, whose
   * segments are already English code slugs (AX-9). Empty at the app root (nothing to address).
   *
   * FUTURE (§7, deferred): this seam will lazily disclose the resource's RBAC/ReBAC access-control
   * graph. Today it renders the slug path only — keep this the single source of the path segments.
   */
  resourcePathSegments(): string[] {
    return this.path.split("/").filter((s) => s.length > 0)
  }

  /** The four primary app-spaces — always in the footer pipeline bar. */
  primaryItems(): NavItem[] {
    return [...PRIMARY_NAV]
  }

  /** The user-menu destinations (review appears once a run exists). */
  userMenuItems(): NavItem[] {
    return USER_MENU_NAV.filter((item) => !item.requiresRun || this.hasRun)
  }

  isActive(item: NavItem): boolean {
    return this.path === item.match || this.path.startsWith(`${item.match}/`)
  }

  isGated(item: NavItem): boolean {
    if (item.gate) return !item.gate(this.session)
    return false
  }

  /** The destination the user is currently in — primary space OR user-menu route. */
  private activeItem(): NavItem | undefined {
    return [...this.primaryItems(), ...this.userMenuItems()].find((item) => this.isActive(item))
  }

  /** The section GROUP whose route family the current path belongs to (longest prefix wins). */
  private sectionGroup(): SectionGroup | undefined {
    const p = this.path
    return [...SECTION_GROUPS]
      .sort((a, b) => b.prefix.length - a.prefix.length)
      .find((g) => p === g.prefix || p.startsWith(`${g.prefix}/`))
  }

  /** The left-pane header title for the current space (its space label, or the app name). */
  sectionTitle(): string {
    const group = this.sectionGroup()
    return group ? this.t(group.titleKey) : this.t("app.name")
  }

  /** The left-pane header icon key (mirrors the space glyph) for the current space. */
  sectionTitleIcon(): SectionIcon {
    const key = this.sectionGroup()?.titleKey
    switch (key) {
      case "nav.workshops":
        return "workshop"
      case "nav.kompass":
        return "overview"
      case "nav.suche":
        return "overview"
      case "nav.bewerbungen":
        return "cv"
      case "nav.onboarding":
        return "upload"
      case "nav.laeufe":
        return "runs"
      case "nav.guthaben":
        return "credits"
      case "nav.einstellungen":
        return "settings"
      default:
        return "overview"
    }
  }

  /** True when there IS a section context to render a left pane for. */
  hasSections(): boolean {
    return this.sectionItems().length > 0
  }

  /**
   * The left-pane sections to render: a surface's CONTRIBUTED items when present (they carry
   * live labels + indicators), else the shell's per-space DEFAULT set resolved through `t()`.
   */
  sectionItems(): SectionNavItem[] {
    const contributed = this.sectionNav.items.value
    if (contributed.length > 0) return [...contributed]
    const group = this.sectionGroup()
    if (!group) return []
    // A surface owns nothing here → the shell's per-space DEFAULT set, with any LIVE
    // indicator (e.g. Belege → open gaps, #32) merged in by URL and its label resolved here.
    const live = this.sectionNav.indicators.value
    return group.items.map((it) => {
      const indicator = live[it.url] ?? it.indicator
      return {
        url: it.url,
        match: it.match,
        label: this.t(it.label),
        icon: it.icon,
        ...(indicator ? { indicator: { ...indicator, label: this.t("sectionnav.indicator.open", { n: indicator.count }) } } : {}),
      }
    })
  }

  /** The match of the deepest section the current path resolves to (longest match wins). */
  private activeSectionMatch(): string | undefined {
    const p = this.path
    return this.sectionItems()
      .filter((it) => p === it.match || p.startsWith(`${it.match}/`))
      .sort((a, b) => b.match.length - a.match.length)[0]?.match
  }

  isSectionActive(item: SectionNavItem): boolean {
    return item.match === this.activeSectionMatch()
  }

  /**
   * Build the breadcrumb tail of the brand lockup: `App-Space › …surface crumbs`
   * (§brand: »P. Career | {Surface} › {Context}«). The shell OWNS the App-Space
   * segment; deeper ones (Sub-area, Step) come from the active surface via the
   * BreadcrumbBus (§7a). The former static Workspace placeholder crumb is gone —
   * the ratified lockup has no workspace segment (the feature stays deferred).
   */
  breadcrumbCrumbs(): Crumb[] {
    const active = this.activeItem()
    // App-Space: current destination label + a dropdown to switch among the 4 spaces.
    const appSpace: Crumb = {
      label: active ? this.t(active.label) : this.t("app.name"),
      choices: PRIMARY_NAV.map((item) => ({ label: this.t(item.label), current: this.isActive(item), url: item.url })),
    }
    return [appSpace, ...this.breadcrumb.crumbs.value]
  }

  /** The match prefix of the active destination — the shell's "surface" identity
   *  (drives the restrained dot-bounce on a surface switch; null when unresolved). */
  activeSpaceMatch(): string | null {
    return this.activeItem()?.match ?? null
  }

  themeMode(): ThemeMode {
    return this.theme.value
  }

  themeModes(): readonly ThemeMode[] {
    return THEME_MODES
  }

  themeLabel(mode: ThemeMode): CareerKey {
    return THEME_LABEL[mode]
  }

  go(url: string): void {
    void this.router.navigate(url)
  }

  /** Apply a theme mode: light/dark set <html data-theme>, auto removes it (follow OS). */
  setTheme(mode: ThemeMode): void {
    this.theme.set(mode)
    const root = globalThis.document?.documentElement
    if (root) {
      if (mode === "auto") root.removeAttribute("data-theme")
      else root.setAttribute("data-theme", mode)
    }
    try {
      globalThis.localStorage?.setItem(THEME_KEY, mode)
    } catch {
      // storage unavailable — the in-memory signal still drives this session.
    }
  }

  isNavCollapsed(): boolean {
    return this.navCollapsed.value
  }

  isDrawerOpen(): boolean {
    return this.navDrawerOpen.value
  }

  isAsideOpen(): boolean {
    return this.asideOpen.value
  }

  /** The far-left control: on MOBILE it opens/closes the overlay drawer; on DESKTOP it
   *  collapses/expands the inline pane to an icon rail (persisted). */
  toggleNav(): void {
    if (isMobileViewport()) {
      this.navDrawerOpen.set(!this.navDrawerOpen.value)
      return
    }
    const next = !this.navCollapsed.value
    this.navCollapsed.set(next)
    try {
      globalThis.localStorage?.setItem(NAV_KEY, next ? "1" : "0")
    } catch {
      // storage unavailable — session-only.
    }
  }

  /** Dismiss both mobile overlays (scrim tap / post-navigation) — desktop rail is untouched. */
  closePanes(): void {
    if (this.navDrawerOpen.value) this.navDrawerOpen.set(false)
    if (this.asideOpen.value) this.toggleAside()
  }

  /** Open/close the right AI-agent pane; persist the choice. */
  toggleAside(): void {
    const next = !this.asideOpen.value
    this.asideOpen.set(next)
    try {
      globalThis.localStorage?.setItem(ASIDE_KEY, next ? "1" : "0")
    } catch {
      // storage unavailable — session-only.
    }
  }

  /** The avatar glyph: a profile-image URL from the IdP, or initials to render as a fallback. */
  avatar(name: string): { picture?: string; initials: string } {
    const picture = this.session.user?.picture
    return { ...(picture ? { picture } : {}), initials: initialsOf(name) }
  }
}

export class CareerApp extends KitElement<ShellVm> {
  /** The destination the brand dot last acknowledged (null until the first render). */
  #lastSpaceMatch: string | null = null

  /**
   * I7: after each render — once the outlet has mounted the new surface — move focus to
   * <main id="main-content"> on a pending user navigation so keyboard/AT users continue INTO
   * the new content. The VM gates this to real navigations (never initial load / same-URL /
   * the workshop-runner's in-surface step jump, which is not a route change). `<main>` is
   * stable across the outlet swap, so focusing it never fights the surface's own render.
   *
   * Brand motion stays RESTRAINED in-app (§brand): the enter timeline plays once on mount
   * (`animated`); a destination switch gets only a subtle dot-bounce — deeper navigation
   * within a space animates nothing. Reduced motion short-circuits inside the element.
   */
  override flushRender(): void {
    super.flushRender()
    if (!this.isConnected) return
    if (this.vm.takePendingMainFocus()) {
      this.querySelector<HTMLElement>("#main-content")?.focus({ preventScroll: false })
    }
    const space = this.vm.activeSpaceMatch()
    if (space !== null) {
      if (this.#lastSpaceMatch !== null && space !== this.#lastSpaceMatch) {
        void this.querySelector("kit-brand-mark")?.play("dot-bounce")
      }
      this.#lastSpaceMatch = space
    }
  }

  protected createViewModel(ctx: KitContext): ShellVm {
    return new ShellVm(
      ctx.inject(RouterToken),
      ctx.inject(I18nToken),
      ctx.inject(SessionToken),
      ctx.inject(IngestStoreToken),
      ctx.inject(BreadcrumbToken),
      ctx.inject(SectionNavToken),
    )
  }

  /** One breadcrumb segment — a plain label, or a `<details>` dropdown of siblings. */
  private crumbSegment(vm: ShellVm, crumb: Crumb): TemplateResult {
    if (!crumb.choices || crumb.choices.length === 0) {
      return html`<span class="crumb crumb-static" lang="de">${crumb.label}</span>`
    }
    return html`<details class="crumb crumb-menu">
      <summary class="crumb-btn" lang="de">
        <span class="crumb-text">${crumb.label}</span>
        <svg class="crumb-caret" viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
      </summary>
      <div class="crumb-pop">
        ${crumb.choices.map((choice) =>
          choice.heading
            ? html`<span class="crumb-group" lang="de">${choice.label}</span>`
            : choice.url !== undefined
            ? html`<a
                class=${classMap({ "crumb-choice": true, "is-current": choice.current })}
                href=${choice.url}
                data-act="nav"
                data-url=${choice.url}
                aria-current=${ifDefined(choice.current ? "true" : undefined)}
                lang="de"
                >${choice.label}</a
              >`
            : html`<button
                type="button"
                class=${classMap({ "crumb-choice": true, "is-current": choice.current })}
                data-act="breadcrumb-step"
                data-step-id=${ifDefined(choice.stepId)}
                aria-current=${ifDefined(choice.current ? "true" : undefined)}
                lang="de"
              >
                ${choice.label}
              </button>`,
        )}
      </div>
    </details>`
  }

  /**
   * The footer resource-path (§7) — the url-safe SLUG path to the asset in view, pinned right of
   * the pipeline spaces, shown only when the bar has room (CSS drops it first on narrow viewports).
   * Distinct from the human breadcrumb; renders slugs, not display-names. A display element now; the
   * future access-control disclosure hangs off this same seam.
   */
  private resourcePath(vm: ShellVm): TemplateResult {
    const segments = vm.resourcePathSegments()
    if (segments.length === 0) return nothing as unknown as TemplateResult
    const last = segments.length - 1
    return html`<div class="bar-respath" aria-label=${vm.t("bar.resourcePath.label")}>
      ${segments.map((seg, i) =>
        html`${i > 0 ? html`<span class="respath-sep" aria-hidden="true">/</span>` : nothing}<span
          class=${classMap({ "respath-seg": true, "respath-leaf": i === last })}
          >${seg}</span
        >`,
      )}
    </div>`
  }

  /**
   * The left-pane account block (native Popover) — the avatar (IdP picture or initials) + name +
   * email + chevron, and the popover that is the home for everything non-pipeline (§7a): Läufe,
   * KI-Einstellungen, Onboarding, Theme, Abmelden.
   */
  private avatarMenu(vm: ShellVm, name: string): TemplateResult {
    const { picture, initials } = vm.avatar(name)
    const email = vm.session.user?.email
    return html`
      <button type="button" class="shell-avatar" popovertarget=${USER_POP_ID} aria-label=${vm.t("user.menu.open")} lang="de">
        <span class="avatar-badge" aria-hidden="true">
          ${picture ? html`<img class="avatar-img" src=${picture} alt="" referrerpolicy="no-referrer" />` : html`<span class="avatar-initials">${initials}</span>`}
        </span>
        <span class="avatar-id">
          <span class="avatar-name" lang="de">${name}</span>
          ${email ? html`<span class="avatar-email">${email}</span>` : nothing}
        </span>
        <svg class="avatar-caret" viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
      </button>
      <div class="user-pop" popover="auto" id=${USER_POP_ID} lang="de">
        <p class="user-pop-head" lang="de">${vm.t("auth.signedInAs", { name })}</p>
        <nav class="user-pop-nav" aria-label=${vm.t("user.menu.title")}>
          ${vm.userMenuItems().map((item) => userMenuAnchor(vm, item))}
        </nav>
        <div class="user-pop-theme" role="group" aria-label=${vm.t("theme.label")}>
          ${vm.themeModes().map(
            (mode) => html`<button
              type="button"
              data-act="set-theme"
              data-theme-set=${mode}
              aria-pressed=${vm.themeMode() === mode ? "true" : "false"}
              lang="de"
            >
              ${vm.t(vm.themeLabel(mode))}
            </button>`,
          )}
        </div>
        <a href="/api/auth/logout" class="user-pop-logout" lang="de">${vm.t("auth.logout")}</a>
      </div>
      <span id=${GATED_HINT_ID} class="visually-hidden" lang="de">${vm.t("gated.hint")}</span>
    `
  }

  /**
   * The credit-balance chip in the user area (foot of the left pane) — a small, theme-following
   * pill linking to /credits. Renders only once a balance is known (hidden while unknown or when
   * billing isn't live). Folds to the wallet glyph alone when the pane collapses to a rail.
   */
  private creditsChip(vm: ShellVm): TemplateResult | typeof nothing {
    const balance = vm.walletBalance()
    if (balance === null) return nothing
    return html`<a
      href="/credits"
      data-act="nav"
      data-url="/credits"
      class="credits-chip"
      aria-label=${vm.t("credits.chip.aria", { n: balance.toLocaleString("de-DE") })}
      title=${vm.t("credits.chip.aria", { n: balance.toLocaleString("de-DE") })}
      lang="de"
    >
      <svg class="credits-chip-icon" viewBox="0 0 24 24" aria-hidden="true">${GLYPH.wallet}</svg>
      <span class="credits-chip-value">${balance.toLocaleString("de-DE")}</span>
      <span class="credits-chip-label">${vm.t("credits.chip.label")}</span>
    </a>`
  }

  /**
   * The shared "insufficient credits" (402) notice — raised by the shared fetch layer for ANY
   * metered action, shown ONCE here (not per surface). An inline, dismissible banner with a link
   * to /credits. Never credits anything; it only points the user at the top-up surface.
   */
  private paymentNotice(vm: ShellVm): TemplateResult | typeof nothing {
    if (!vm.showPaymentNotice()) return nothing
    return html`
      <div class="pay-notice" role="alert">
        <svg class="pay-notice-icon" viewBox="0 0 24 24" aria-hidden="true">${GLYPH.wallet}</svg>
        <span class="pay-notice-text" lang="de">${vm.t(vm.paymentNoticeKey())}</span>
        <a class="pay-notice-cta" href="/credits" data-act="nav" data-url="/credits" lang="de">${vm.t("credits.notice.cta")}</a>
        <button
          type="button"
          class="pay-notice-dismiss"
          data-act="dismiss-payment-notice"
          aria-label=${vm.t("credits.notice.dismiss")}
          lang="de"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
        </button>
      </div>
    `
  }

  /**
   * The right pane — the Platform AI Agent panel. It frames the plattform-wide agent and the
   * Platform Applications the user has access to (Career Studio being one; the list grows as
   * access is granted). The composer is an honest coming-soon placeholder — the concept is
   * represented correctly; wiring the real agent + full app roster is future work.
   */
  private agentPanel(vm: ShellVm): TemplateResult {
    return html`
      <div class="agent-head">
        <span class="agent-mark" aria-hidden="true">
          <svg viewBox="0 0 24 24"><path d="M12 3v4M12 17v4M3 12h4M17 12h4" /><circle cx="12" cy="12" r="4" /></svg>
        </span>
        <span class="agent-heading">
          <span class="agent-title" lang="de">${vm.t("ai.panel.title")}</span>
          <span class="agent-sub" lang="de">${vm.t("ai.panel.subtitle")}</span>
        </span>
      </div>
      <div class="agent-body">
        <p class="agent-section-label" lang="de">${vm.t("ai.panel.apps")}</p>
        <ul class="agent-apps" role="list">
          <li class="agent-app is-current">
            <span class="agent-app-dot" aria-hidden="true"></span>
            <span class="agent-app-name" lang="de">${vm.t("brand.name")}</span>
            <span class="agent-app-tag" lang="de">${vm.t("ai.panel.thisApp")}</span>
          </li>
        </ul>
        <p class="agent-more" lang="de">${vm.t("ai.panel.moreSoon")}</p>
      </div>
      <div class="agent-composer">
        <div class="agent-input" aria-hidden="true">${vm.t("ai.panel.composerPlaceholder")}</div>
        <span class="agent-soon" lang="de">${vm.t("ai.panel.comingSoon")}</span>
      </div>
    `
  }

  protected render(vm: ShellVm): TemplateResult {
    const { user } = vm.session
    const loginHref = `/api/auth/login?returnTo=${encodeURIComponent(vm.path)}`
    const crumbs = vm.breadcrumbCrumbs()
    const navCollapsed = vm.isNavCollapsed()
    const asideOpen = vm.isAsideOpen()
    const hasSections = vm.hasSections()
    const sections = vm.sectionItems()
    return html`
      <div
        class="shell"
        data-nav=${navCollapsed ? "collapsed" : "expanded"}
        data-drawer=${vm.isDrawerOpen() ? "open" : "closed"}
        data-aside=${asideOpen ? "open" : "closed"}
        data-sections=${hasSections ? "yes" : "no"}
      >
        <a class="skip-link" href="#main-content">${vm.t("a11y.skipToContent" as CareerKey)}</a>
        <!-- I7: a polite live region announces the destination surface on a route change
             (moves focus to #main-content too). Visually hidden; not focusable, so the
             skip-link stays first in the tab order. -->
        <p class="visually-hidden" aria-live="polite" aria-atomic="true" lang="de">${vm.announcementText()}</p>

        <header class="shell-head">
          <button
            type="button"
            class="chrome-toggle chrome-toggle-nav"
            data-act="toggle-nav"
            aria-label=${navCollapsed ? vm.t("nav.expandSidebar") : vm.t("nav.collapseSidebar")}
            aria-expanded=${navCollapsed ? "false" : "true"}
            lang="de"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">${GLYPH.sidebarLeft}</svg>
          </button>
          <a class="shell-brand link-reset" href="/search" data-act="nav" data-url="/search" aria-label=${vm.t("brand.name")}>
            <!-- The platform P-mark, bare (no in-gap name at this size — §brand: the shell
                 lockup is »P. Career | {Surface} › {Context}«, the full in-gap lockup is
                 reserved for large surfaces like the login). aria-hidden: the link label
                 already names the brand. "animated" plays the enter timeline once on mount. -->
            <kit-brand-mark space="personal" animated aria-hidden="true"></kit-brand-mark>
            <span class="shell-brand-word">${vm.t("app.name")}</span>
          </a>
          <span class="shell-lockup-divider" aria-hidden="true"></span>
          <nav class="shell-crumbs" aria-label=${vm.t("breadcrumb.label")}>
            ${crumbs.map((crumb, i) =>
              html`${i > 0 ? html`<span class="crumb-sep" aria-hidden="true">›</span>` : nothing}${this.crumbSegment(vm, crumb)}`,
            )}
          </nav>
          <button
            type="button"
            class="chrome-toggle chrome-toggle-aside"
            data-act="toggle-aside"
            aria-label=${vm.t("ai.panel.toggle")}
            aria-expanded=${asideOpen ? "true" : "false"}
            aria-pressed=${asideOpen ? "true" : "false"}
            lang="de"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">${GLYPH.sidebarRight}</svg>
          </button>
        </header>

        <nav class="shell-nav" aria-label=${vm.t("sectionnav.label")}>
          <div class="nav-space-head" aria-hidden=${navCollapsed ? "true" : "false"}>
            <span class="nav-space-icon">${sectionIconGlyph(vm.sectionTitleIcon())}</span>
            <span class="nav-space-title" lang="de">${vm.sectionTitle()}</span>
          </div>
          <div class="nav-group">
            ${hasSections
              ? sections.map((item) => sectionAnchor(vm, item))
              : html`<p class="nav-empty" lang="de">${vm.t("sectionnav.empty")}</p>`}
          </div>
          <div class="nav-spacer"></div>
          <div class="nav-foot">
            ${user ? this.creditsChip(vm) : nothing}
            ${user
              ? this.avatarMenu(vm, user.name ?? user.sub)
              : html`<a href=${loginHref} class="nav-item nav-signin" lang="de">${navIcon("nav.profil")}<span class="nav-item-label">${vm.t("auth.login")}</span></a>`}
          </div>
        </nav>

        <main id="main-content" tabindex="-1" class="shell-main">
          ${this.paymentNotice(vm)}
          <kit-outlet></kit-outlet>
        </main>

        <aside class="shell-aside" aria-label=${vm.t("ai.panel.title")} aria-hidden=${asideOpen ? "false" : "true"}>
          ${this.agentPanel(vm)}
        </aside>

        <footer class="shell-foot">
          <nav class="foot-nav" aria-label=${vm.t("bar.nav.label")}>
            ${vm.primaryItems().map((item) => footAnchor(vm, item))}
          </nav>
          ${this.resourcePath(vm)}
        </footer>

        <!-- Mobile scrim — taps close whichever pane is open (left drawer or AI panel). -->
        <button
          type="button"
          class="shell-scrim"
          data-act="close-panes"
          aria-label=${vm.t("nav.collapseSidebar")}
          tabindex="-1"
        ></button>
      </div>
    `
  }

  protected override actions(vm: ShellVm): ActionMap {
    return {
      nav: ({ url }, event) => {
        event.preventDefault() // client-side navigation, no reload
        // Dismiss any open menu the click came from (user-menu popover or a crumb dropdown).
        const target = event.target as Element | null
        ;(target?.closest?.("[popover]") as HTMLElement | null)?.hidePopover?.()
        const det = target?.closest?.("details[open]") as HTMLDetailsElement | null
        if (det) det.open = false
        if (url) vm.go(url)
        // Navigating away resolves the "insufficient credits" prompt (the user has moved on / is topping up).
        vm.dismissPaymentNotice()
        // On mobile, moving to a destination closes the overlay drawer behind it.
        if (vm.isDrawerOpen()) vm.closePanes()
      },
      "set-theme": ({ themeSet }) => {
        if (themeSet === "light" || themeSet === "dark" || themeSet === "auto") vm.setTheme(themeSet)
      },
      "toggle-nav": () => vm.toggleNav(),
      "toggle-aside": () => vm.toggleAside(),
      // The scrim shows on mobile only when a pane is open; a tap dismisses both overlays.
      "close-panes": () => vm.closePanes(),
      "dismiss-payment-notice": () => vm.dismissPaymentNotice(),
      "breadcrumb-step": ({ stepId }, event) => {
        // Jump the workshop log to the addressed step node (urn-bound), no route change.
        const det = (event.target as Element | null)?.closest?.("details[open]") as HTMLDetailsElement | null
        if (det) det.open = false
        if (!stepId) return
        globalThis.document
          ?.querySelector(`.timeline-node[urn="urn:career:workshop-step:${stepId}"]`)
          ?.scrollIntoView({ behavior: "smooth", block: "start" })
      },
    }
  }
}
